
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useMasterLicenseSettings } from '@/contexts/MasterLicenseSettingsContext';
import { useToast } from '@/hooks/use-toast';
import EmailConfigForm, { EmailConfigFormValues } from './email/EmailConfigForm';
import EmailConfigList from './email/EmailConfigList';

const EmailSettingsTab: React.FC = () => {
  const { toast } = useToast();
  const { 
    licenseSettings, 
    handleEmailConfigAdd, 
    handleEmailConfigUpdate, 
    handleEmailConfigDelete,
    handleTestEmailConnection,
    isSavingEmailConfig,
    isTestingEmailConnection
  } = useMasterLicenseSettings();
  
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const emailConfigs = licenseSettings.emailConfigs || [];

  // Edit an existing email configuration
  const handleEditConfig = (configId: string) => {
    const config = emailConfigs.find(config => config.id === configId);
    if (config) {
      setEditingConfigId(configId);
      setShowAddForm(true);
    }
  };
  
  // Handle form submission
  const handleSaveConfig = async (values: EmailConfigFormValues) => {
    try {
      if (editingConfigId) {
        // Update existing config
        await handleEmailConfigUpdate(editingConfigId, values);
        toast({
          title: "Email Configuration Updated",
          description: `Email configuration "${values.name}" has been updated successfully.`
        });
      } else {
        // Add new config
        if (emailConfigs.length >= 5) {
          toast({
            title: "Maximum Configurations Reached",
            description: "You can only have up to 5 email configurations.",
            variant: "destructive"
          });
          return;
        }
        
        await handleEmailConfigAdd(values);
        toast({
          title: "Email Configuration Added",
          description: `Email configuration "${values.name}" has been added successfully.`
        });
      }
      
      // Reset form and state
      setEditingConfigId(null);
      setShowAddForm(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save email configuration.",
        variant: "destructive"
      });
    }
  };
  
  // Set a configuration as default
  const handleSetDefault = (configId: string) => {
    emailConfigs.forEach(config => {
      const newIsDefault = config.id === configId;
      if (config.isDefault !== newIsDefault) {
        handleEmailConfigUpdate(config.id, { isDefault: newIsDefault });
      }
    });
    
    toast({
      title: "Default Email Updated",
      description: "The default email configuration has been updated."
    });
  };
  
  // Toggle a configuration's active status
  const handleToggleActive = (configId: string, currentStatus: boolean) => {
    handleEmailConfigUpdate(configId, { isActive: !currentStatus });
    
    toast({
      title: currentStatus ? "Email Disabled" : "Email Enabled",
      description: `The email configuration has been ${currentStatus ? "disabled" : "enabled"}.`
    });
  };
  
  // Delete an email configuration
  const handleDeleteConfig = (configId: string) => {
    if (confirm("Are you sure you want to delete this email configuration?")) {
      handleEmailConfigDelete(configId);
      toast({
        title: "Email Configuration Deleted",
        description: "The email configuration has been deleted successfully."
      });
    }
  };
  
  // Test email connection
  const handleTestConnection = async (values: EmailConfigFormValues) => {
    try {
      const success = await handleTestEmailConnection(values);
      
      toast({
        title: success ? "Connection Successful" : "Connection Failed",
        description: success 
          ? "Email configuration test was successful." 
          : "Failed to connect using these settings. Please check your credentials.",
        variant: success ? "default" : "destructive",
      });
    } catch (error) {
      toast({
        title: "Connection Test Error",
        description: error instanceof Error ? error.message : "Failed to test connection",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Settings</CardTitle>
        <CardDescription>
          Configure SMTP email settings for sending notifications, invitations, and other communications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Email Configurations</h3>
          {!showAddForm && emailConfigs.length < 5 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setEditingConfigId(null);
                setShowAddForm(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Email Configuration
            </Button>
          )}
        </div>
        
        {/* List of configured emails */}
        {!showAddForm && (
          <EmailConfigList
            emailConfigs={emailConfigs}
            onAddNew={() => {
              setEditingConfigId(null);
              setShowAddForm(true);
            }}
            onEdit={handleEditConfig}
            onToggleActive={handleToggleActive}
            onSetDefault={handleSetDefault}
            onDelete={handleDeleteConfig}
          />
        )}
        
        {/* Add/Edit Email Configuration Form */}
        {showAddForm && (
          <Card className="border rounded-md mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl">
                {editingConfigId ? 'Edit Email Configuration' : 'Add Email Configuration'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EmailConfigForm
                config={editingConfigId 
                  ? emailConfigs.find(config => config.id === editingConfigId) 
                  : undefined}
                onCancel={() => {
                  setShowAddForm(false);
                  setEditingConfigId(null);
                }}
                onSave={handleSaveConfig}
                onTestConnection={handleTestConnection}
                isSaving={isSavingEmailConfig}
                isTesting={isTestingEmailConnection}
              />
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailSettingsTab;
